<template>
  <div class="pycad-save">
    <div class="pycad-save__header">
      <div class="pycad-save__icon">
        <v-icon size="20">mdi-content-save-outline</v-icon>
      </div>
      <div class="pycad-save__heading">
        <div class="pycad-save__title">Save Session</div>
        <div class="pycad-save__subtitle">
          Download everything — images, segmentations, annotations, and layout —
          as a single file you can reload later.
        </div>
      </div>
    </div>

    <form class="pycad-save__form" @submit.prevent="saveSession">
      <label class="pycad-save__label" for="session-state-filename">
        Filename
      </label>
      <div class="pycad-save__field" :class="{ 'has-error': !valid }">
        <v-icon size="16" class="pycad-save__field-icon">
          mdi-file-outline
        </v-icon>
        <input
          id="session-state-filename"
          v-model="fileName"
          type="text"
          spellcheck="false"
          autocomplete="off"
          @input="validate"
        />
      </div>
      <div v-if="!valid" class="pycad-save__error">Filename is required.</div>
    </form>

    <div class="pycad-save__actions">
      <button type="button" class="pycad-save__btn-cancel" @click="close()">
        Cancel
      </button>
      <button
        type="button"
        class="pycad-save__btn-save"
        :disabled="!valid || saving"
        @click="saveSession"
      >
        <v-icon v-if="!saving" size="16">mdi-download-outline</v-icon>
        <v-icon v-else size="16" class="pycad-save__spinner">
          mdi-loading
        </v-icon>
        <span data-testid="save-session-confirm-button">
          {{ saving ? 'Saving...' : 'Save' }}
        </span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { saveAs } from 'file-saver';
import { onKeyDown } from '@vueuse/core';

import { serialize } from '../io/state-file/serialize';
import { useMessageStore } from '../store/messages';

const DEFAULT_FILENAME = 'session.smilesuite.zip';

export default defineComponent({
  props: {
    close: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const fileName = ref(DEFAULT_FILENAME);
    const valid = ref(true);
    const saving = ref(false);

    function validate() {
      valid.value = fileName.value.trim().length > 0;
    }

    async function saveSession() {
      validate();
      if (!valid.value || saving.value) return;

      saving.value = true;
      try {
        const blob = await serialize();
        saveAs(blob, fileName.value.trim());
        props.close();
      } catch (err) {
        const messageStore = useMessageStore();
        messageStore.addError('Failed to save session', {
          error: err instanceof Error ? err : new Error(String(err)),
        });
      } finally {
        saving.value = false;
      }
    }

    onMounted(() => {
      const input = document.getElementById(
        'session-state-filename'
      ) as HTMLInputElement | null;
      if (input) {
        input.focus();
        // Preselect the name portion so typing replaces it but keeps ".zip".
        const stem = fileName.value.lastIndexOf('.zip');
        input.setSelectionRange(0, stem > 0 ? stem : fileName.value.length);
      }
    });

    onKeyDown('Enter', () => {
      saveSession();
    });

    return {
      saving,
      saveSession,
      fileName,
      valid,
      validate,
    };
  },
});
</script>

<style scoped>
.pycad-save {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: var(--pycad-elevated, #161616);
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius, 12px);
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--pycad-text-primary, #fff);
}

.pycad-save__header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  /* keep clear of the dialog's floating close button */
  padding-right: 40px;
}

.pycad-save__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: var(--pycad-radius-sm, 8px);
  background: var(--pycad-primary-dim, rgba(255, 153, 0, 0.12));
  border: 1px solid var(--pycad-primary-border, rgba(255, 153, 0, 0.3));
}

.pycad-save__icon :deep(.v-icon) {
  color: var(--pycad-primary, #ff9900);
}

.pycad-save__title {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.pycad-save__subtitle {
  margin-top: 4px;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--pycad-text-secondary, #a0a0a0);
}

.pycad-save__form {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pycad-save__label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--pycad-text-secondary, #a0a0a0);
}

.pycad-save__field {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  background: var(--pycad-surface, #0b0b0b);
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius-sm, 8px);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.pycad-save__field:focus-within {
  border-color: var(--pycad-primary-border, rgba(255, 153, 0, 0.45));
  box-shadow: 0 0 0 3px rgba(255, 153, 0, 0.08);
}

.pycad-save__field.has-error {
  border-color: rgba(255, 92, 92, 0.55);
}

.pycad-save__field-icon {
  color: var(--pycad-text-muted, #666);
  flex-shrink: 0;
}

.pycad-save__field input {
  flex: 1;
  min-width: 0;
  padding: 11px 0;
  font-size: 13.5px;
  font-family: inherit;
  color: var(--pycad-text-primary, #fff);
  background: transparent;
  border: none;
  outline: none;
}

.pycad-save__field input::selection {
  background: rgba(255, 153, 0, 0.3);
}

.pycad-save__error {
  font-size: 12px;
  color: #ff5c5c;
}

.pycad-save__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.pycad-save__btn-cancel,
.pycad-save__btn-save {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  letter-spacing: 0.02em;
  border-radius: var(--pycad-radius-sm, 8px);
  cursor: pointer;
  transition:
    filter 0.15s ease,
    background 0.15s ease,
    border-color 0.15s ease;
}

.pycad-save__btn-cancel {
  color: var(--pycad-text-secondary, #a0a0a0);
  background: transparent;
  border: 1px solid var(--pycad-border, #282828);
}

.pycad-save__btn-cancel:hover {
  border-color: var(--pycad-text-muted, #666);
  color: var(--pycad-text-primary, #fff);
}

.pycad-save__btn-save {
  color: #111;
  background: var(--pycad-primary, #ff9900);
  border: 1px solid transparent;
}

.pycad-save__btn-save :deep(.v-icon) {
  color: #111;
}

.pycad-save__btn-save:hover:not(:disabled) {
  filter: brightness(1.08);
}

.pycad-save__btn-save:disabled {
  opacity: 0.5;
  cursor: default;
}

.pycad-save__spinner {
  animation: pycad-save-spin 0.9s linear infinite;
}

@keyframes pycad-save-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
